import type { MultipartUploadHandle } from "@/client/media-upload";

import { UploadTask } from "./upload-queue-slice";

/**
 * How many bytes of recordings the registry holds before the queue starts
 * dropping attempts the student has already replaced. Above the 200 MB cap in
 * `use-socratic-recording.ts`, so one recording is always admitted.
 */
export const UPLOAD_MEMORY_BUDGET_BYTES = 250 * 1024 * 1024;

export interface UploadRegistryEntry {
  file: File;
  /** Null until the drain loop starts the upload. */
  handle: MultipartUploadHandle | null;
}

/**
 * The registry of video response uploads and its upload handle
 *
 * An entry lives from the moment the recording is captured until its media is
 * uploaded or its task leaves the queue, whichever is first.
 */
export class UploadRegistry {
  #entries = new Map<string, UploadRegistryEntry>();

  insert(turnId: string, file: File): void {
    this.#entries.set(turnId, { file, handle: null });
  }

  lookup(turnId: string): UploadRegistryEntry | undefined {
    return this.#entries.get(turnId);
  }

  attachHandle(turnId: string, handle: MultipartUploadHandle): void {
    const entry = this.#entries.get(turnId);
    // No entry means the task already left the queue, and the registry has no
    // file to build one around.
    if (!entry) return;
    entry.handle = handle;
  }

  delete(turnId: string): void {
    this.#entries.delete(turnId);
  }

  byteSize(): number {
    let bytes = 0;
    for (const entry of this.#entries.values()) {
      bytes += entry.file.size;
    }
    return bytes;
  }
}

/** The app's one registry, shared by the recording hook and the drain loop. */
export const uploadRegistry = new UploadRegistry();

export type EvictionDecision =
  | { kind: "within-budget" }
  /** Drop this task: delete its entry, remove the task. */
  | { kind: "evict"; task: UploadTask }
  /** Over budget with only unreplaced recordings left. Stay over, and report it. */
  | { kind: "over-budget" };

/**
 * The eviction rule. The budget is not a hard ceiling - it only decides when to
 * give up on a dialogue the student has already recorded a new attempt for.
 *
 * `tasks` is the queue in its own order, oldest first.
 */
export function chooseEviction(
  tasks: UploadTask[],
  registryBytes: number
): EvictionDecision {
  if (registryBytes <= UPLOAD_MEMORY_BUDGET_BYTES) {
    return { kind: "within-budget" };
  }

  const task = tasks.find(
    (t) => t.superseded && t.status === "queued" && !t.media
  );

  return task ? { kind: "evict", task } : { kind: "over-budget" };
}
